import axios from "axios";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

const AIRPORTDB_TOKEN = process.env.REACT_APP_AIRPORTDB_API_KEY || "";
const CHECKWX_KEY = process.env.REACT_APP_CHECKWX_API_KEY || "";

const ICAO_MAPPING = {
  UZTT: "UTTT",
  UZSS: "UTSS",
  UZSB: "UTSB",
  UZNU: "UTNU",
  UZNN: "UTNN",
  UZFF: "UTFF",
  UZFA: "UTFA",
  UZFN: "UTFN",
  UZST: "UTST",
  UZSK: "UTSK",
  UZSA: "UTSA",
};

const useAirportFetch = ({ onLoading, onError, onLoaded }) => {
  const { t } = useTranslation();

  const fetch = useCallback(
    async (icaoValue) => {
      const icao = icaoValue.trim().toUpperCase();
      const airportDbIcao = ICAO_MAPPING[icao] || icao;
      
      if (!icao || icao.length < 3 || icao.length > 6) {
        onError(t("errors.invalidIcao"));
        return;
      }

      if (onLoading) onLoading(true);
      onError(null);

      try {
        const [responseAirport, responseWeather] = await Promise.all([
          axios.get(
            `https://airportdb.io/api/v1/airport/${airportDbIcao}?apiToken=${AIRPORTDB_TOKEN}`
          ),
          axios.get(
            `https://api.checkwx.com/metar/${icao}/decoded`,
            { headers: { "x-api-key": CHECKWX_KEY } }
          ),
        ]);

        const weatherData = responseWeather.data?.data?.[0];
        if (!weatherData) {
          throw new Error("No weather data");
        }

        const data = {
          name: responseAirport.data.name,
          metar: weatherData.raw_text,
          runways: responseAirport.data.runways,
          wind_direction: weatherData.wind?.degrees ?? 0,
          wind_speed: weatherData.wind?.speed_kts ?? 0,
          temperature: weatherData.temperature?.celsius ?? null,
          humidity: weatherData.humidity ?? null,
          pressure: weatherData.barometer?.hpa ?? null,
          visibility: weatherData.visibility?.meters ?? null,
          flight_category: weatherData.flight_category ?? "UNKNOWN",
          conditions: weatherData.conditions ?? [],
          clouds: weatherData.clouds ?? [],
          icao: icao,
          iata: responseAirport.data.iata_code,
          wikipedia_link: responseAirport.data.wikipedia_link,
          station: responseAirport.data.station,
          time: weatherData.observed,
        };

        if (data.error) {
          onError(data.error);
          if (onLoading) onLoading(false);
          return;
        }

        if (onLoading) onLoading(false);
        onLoaded(data);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("[useAirportFetch]", err);
        }
        const status = err?.response?.status;
        if (status === 404) {
          onError(t("errors.airportNotFound"));
        } else if (status === 401 || status === 403) {
          onError(t("errors.apiKeyInvalid"));
        } else {
          onError(t("error"));
        }
        if (onLoading) onLoading(false);
      }
    },
    [t, onLoading, onError, onLoaded]
  );

  return fetch;
};

export default useAirportFetch;
